import { useState, useEffect, useCallback, memo } from 'react'
import { Search, Car, Edit2, Check, Download, AlertTriangle } from 'lucide-react'
import { getUsers, updateUser } from '../services/userService.js'
import { useNavigate, Link } from 'react-router-dom'
import { useToast } from '../components/Toast.jsx'
import Pagination from '../components/Pagination.jsx'
import Modal from '../components/Modal.jsx'
import FormField from '../components/FormField.jsx'
import ConfirmDialog from '../components/ConfirmDialog.jsx'
import { validateUserForm, hasErrors } from '../utils/validation.js'
import { formatDate } from '../utils/dates.js'
import { exportToCsv } from '../utils/csv.js'
import { DEFAULT_PAGE_SIZE } from '../constants/statusColors.js'

const UserRow = memo(({ user, onEdit, onViewVehicles }) => (
  <tr className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
    <td className="px-6 py-4">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold" aria-label={`Avatar for ${user.name || user.email}`}>
          {(user.name?.[0] || user.email?.[0] || '?').toUpperCase()}
        </div>
        <Link to={`/users/${user.id}`} className="font-medium text-sm text-blue-400 hover:text-blue-300 transition-colors">{user.name || 'No name'}</Link>
      </div>
    </td>
    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{user.email}</td>
    <td className="px-6 py-4 text-center">
      {user.emailVerified
        ? <span className="text-green-400 text-sm font-medium" aria-label="Verified">&#10003;</span>
        : <span className="text-red-400 text-sm font-medium" aria-label="Not verified">&#10007;</span>}
    </td>
    <td className="px-6 py-4 text-center text-sm">{user.vehicleCount ?? 0}</td>
    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{formatDate(user.createdAt)}</td>
    <td className="px-6 py-4 text-right">
      <div className="flex items-center justify-end gap-2">
        <button onClick={() => onEdit(user)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" aria-label={`Edit ${user.name || user.email}`}>
          <Edit2 className="w-4 h-4 text-gray-400" />
        </button>
        <button onClick={() => onViewVehicles(user.id)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" aria-label={`View vehicles for ${user.name || user.email}`}>
          <Car className="w-4 h-4 text-gray-400" />
        </button>
      </div>
    </td>
  </tr>
))
UserRow.displayName = 'UserRow'

const UsersPage = () => {
  const [users, setUsers] = useState([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editUser, setEditUser] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [formErrors, setFormErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [confirmClose, setConfirmClose] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const navigate = useNavigate()
  const toast = useToast()

  const fetchUsers = useCallback(() => {
    setLoading(true)
    setError(null)
    getUsers(page, DEFAULT_PAGE_SIZE, search)
      .then((res) => {
        const data = res.data
        setUsers(data.content || [])
        setTotalPages(data.totalPages || 0)
        setTotalElements(data.totalElements || 0)
      })
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false))
  }, [page, search])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput); setPage(0) }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const openEdit = useCallback((user) => {
    setEditUser(user)
    setEditForm({ name: user.name || '', email: user.email || '', emailVerified: user.emailVerified })
    setFormErrors({})
    setSaveError(null)
    setFormDirty(false)
  }, [])

  const handleCloseModal = useCallback(() => {
    if (formDirty) {
      setConfirmClose(true)
    } else {
      setEditUser(null)
    }
  }, [formDirty])

  const updateField = useCallback((field, value) => {
    setEditForm(f => ({ ...f, [field]: value }))
    setFormDirty(true)
    setFormErrors(prev => ({ ...prev, [field]: undefined }))
  }, [])

  const saveEdit = async () => {
    const errors = validateUserForm(editForm)
    if (hasErrors(errors)) {
      setFormErrors(errors)
      return
    }
    if (!editUser) return
    setSaving(true)
    setSaveError(null)
    try {
      await updateUser(editUser.id, editForm)
      setEditUser(null)
      toast.success('User updated successfully')
      fetchUsers()
    } catch {
      setSaveError('Failed to update user. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleExport = useCallback(() => {
    const headers = ['Name', 'Email', 'Verified', 'Vehicles', 'Joined']
    const rows = users.map(u => [u.name || '', u.email, u.emailVerified ? 'Yes' : 'No', u.vehicleCount ?? 0, formatDate(u.createdAt)])
    exportToCsv('users.csv', headers, rows)
    toast.success(`Exported ${users.length} users to CSV`)
  }, [users, toast])

  const handleViewVehicles = useCallback((userId) => {
    navigate(`/users/${userId}/vehicles`)
  }, [navigate])

  const emptyMessage = search
    ? `No users match "${search}"`
    : 'No users found'

  return (
    <div className="space-y-4">
      {/* Search + Export */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search by name, email or Auth0 ID..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            aria-label="Search users"
          />
        </div>
        <button onClick={handleExport} disabled={users.length === 0}
          className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-30"
          aria-label="Export users to CSV">
          <Download className="w-4 h-4" /> Export
        </button>
        <span className="text-sm text-gray-400">{totalElements} users total</span>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
          <button onClick={fetchUsers} className="ml-auto underline hover:no-underline">Retry</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th scope="col" className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">User</th>
              <th scope="col" className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Email</th>
              <th scope="col" className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Verified</th>
              <th scope="col" className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Vehicles</th>
              <th scope="col" className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Joined</th>
              <th scope="col" className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12 text-gray-400">Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-gray-400">{emptyMessage}</td></tr>
            ) : (
              users.map((user) => (
                <UserRow key={user.id} user={user} onEdit={openEdit} onViewVehicles={handleViewVehicles} />
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} totalElements={totalElements} onPageChange={setPage} label="users" />

      {/* Edit Modal */}
      <Modal open={!!editUser} title="Edit User" onClose={handleCloseModal} footer={
        <>
          <button onClick={handleCloseModal} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">Cancel</button>
          <button onClick={saveEdit} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-sm">
            <Check className="w-4 h-4" />{saving ? 'Saving...' : 'Save'}
          </button>
        </>
      }>
        {saveError && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">{saveError}</div>
        )}
        <FormField label="Name" htmlFor="edit-user-name" error={formErrors.name}>
          <input id="edit-user-name" type="text" value={editForm.name || ''} onChange={(e) => updateField('name', e.target.value)}
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </FormField>
        <FormField label="Email" htmlFor="edit-user-email" error={formErrors.email}>
          <input id="edit-user-email" type="email" value={editForm.email || ''} onChange={(e) => updateField('email', e.target.value)}
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </FormField>
        <div className="flex items-center gap-3">
          <label htmlFor="edit-user-verified" className="text-sm font-medium text-gray-500 dark:text-gray-400">Email Verified</label>
          <button
            id="edit-user-verified"
            role="switch"
            aria-checked={editForm.emailVerified}
            onClick={() => updateField('emailVerified', !editForm.emailVerified)}
            className={`w-10 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${editForm.emailVerified ? 'bg-green-500' : 'bg-gray-600'}`}>
            <div className={`w-4 h-4 bg-white rounded-full transition-transform ml-1 ${editForm.emailVerified ? 'translate-x-4' : ''}`} />
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmClose}
        title="Unsaved changes"
        message="You have unsaved changes. Are you sure you want to close?"
        confirmLabel="Discard"
        onConfirm={() => { setConfirmClose(false); setEditUser(null) }}
        onCancel={() => setConfirmClose(false)}
      />
    </div>
  )
}

export default UsersPage
